import { Box, Typography, styled } from "@mui/material"
import type { BoxProps, SxProps, Theme } from "@mui/material"
import type { ReactNode } from "react"
import { fileExtension } from "helpers/formatters/file-extension"
import { formatArtifactSize } from "helpers/formatters/file-size"

export const ATTACHMENT_CHIP_WIDTH = 220

const ChipRoot = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  width: ATTACHMENT_CHIP_WIDTH,
  padding: theme.spacing(0.5, 0.25, 0.5, 0.75),
  border: "1px solid var(--app-overlay-border)",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "var(--app-bg-paper)",
  color: "var(--mui-palette-text-primary)",
}))

const OneLine = styled(Typography)({
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
})

const ICON_SLOT_SX = { display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, flexShrink: 0 } satisfies SxProps<Theme>
const TEXT_COLUMN_SX = { display: "flex", flexDirection: "column", flex: 1, minWidth: 0 } satisfies SxProps<Theme>

function attachmentTypeLabel(filename: string, mediaType: string): string {
  const extension = fileExtension(filename)
  if (extension !== undefined) return extension.toUpperCase()
  const [, subtype] = mediaType.split("/")
  return subtype !== undefined && subtype !== "" ? subtype.toUpperCase() : "FILE"
}

export function attachmentDetail(filename: string, mediaType: string, sizeBytes?: number): string {
  const type = attachmentTypeLabel(filename, mediaType)
  if (sizeBytes === undefined) return type
  const size = formatArtifactSize(sizeBytes)
  return size === "" ? type : `${type} · ${size}`
}

export interface AttachmentChipProps extends BoxProps {
  icon: ReactNode
  name: string
  detail: string
  detailTitle?: string
  hasFailed?: boolean
  action?: ReactNode
}

export function AttachmentChip({ icon, name, detail, detailTitle, hasFailed = false, action, ...rootProps }: AttachmentChipProps) {
  return (
    <ChipRoot {...rootProps}>
      <Box sx={ICON_SLOT_SX}>{icon}</Box>
      <Box sx={TEXT_COLUMN_SX}>
        <OneLine variant="caption" title={name} color={hasFailed ? "error" : "text.primary"}>{name}</OneLine>
        <OneLine variant="caption" title={detailTitle} color={hasFailed ? "error" : "text.secondary"}>{detail}</OneLine>
      </Box>
      {action}
    </ChipRoot>
  )
}
